/**
 * SourceScopePicker — unified composer chip that narrows the agent's
 * retrieval scope to a curated subset of Drive folders AND/OR specific
 * documents (June 2026).
 *
 * The component supersedes the deprecated standalone ``FolderPicker``
 * by tabbing folder and document selection inside a single popover. UX
 * rationale:
 *
 *   - One affordance ("Ambito") instead of two parallel chips → cleaner
 *     composer Bar, matches Linear/Perplexity scope-pill pattern.
 *   - Same multi-select / pending / apply UX both tabs → muscle memory
 *     carries over between sources.
 *   - Files selected here ride the existing ``mentions[]`` wire field
 *     with ``kind: "document"`` (no new BE contract needed — see
 *     ``ScopedDocument`` schema in ``@ando/core/domain``).
 *
 * Accessibility (WAI-ARIA APG 1.2 ``tabs`` + ``group``):
 *   - tablist with roving tabindex + Arrow/Home/End keyboard nav
 *   - ``role="group"`` on the rows container (compatible with
 *     ``<input type="checkbox">`` children — listbox+checkbox is invalid)
 *   - ``aria-live="polite"`` on empty-result + no-search messages
 *   - focus auto-routed to the active tab's search input on switch
 *
 * The primitive is purely presentational. The caller owns:
 *   - folder list fetching (typically via ``useDriveFolders``),
 *   - document search (debounced, typically via ``useMentionSearch``
 *     scoped to ``kinds=["document"]``),
 *   - persistence (Zustand ``ConversationConfig.folderFilter`` +
 *     ``ConversationConfig.scopedDocuments``).
 *
 * Selection is committed only on "Applica" — pending state lives inside
 * the popover so the user can cancel via Esc / outside-click without
 * mutating the conversation config.
 */

import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { Database, FileText, Folder } from "lucide-react";
import * as React from "react";

import { cn } from "../../lib/cn";
import { Popover, PopoverContent, PopoverTrigger } from "../../primitives/popover";
import { type ConnectorOption, ConnectorPicker } from "./ConnectorPicker";
import {
  SCOPE_PROVIDER_LABEL,
  type ScopeFolderProvider,
  ScopeNoResults,
  ScopePickerEmpty,
  ScopePickerSkeleton,
  ScopeRow,
  ScopeSearchBox,
  ScopeSectionHeader,
  scopeDocSourceLabel,
} from "./scope-shared";

export type { ScopeFolderProvider } from "./scope-shared";
export type { ConnectorOption } from "./ConnectorPicker";

export interface ScopeFolderItem {
  readonly id: string;
  readonly name: string;
  /** Optional full path shown muted next to the name. */
  readonly path?: string;
  readonly provider: ScopeFolderProvider;
}

/**
 * Source slug for a document row. Mirrors the BE ``provider`` field —
 * a white-label union that covers Drives + manual + email/chat
 * attachments + AI-generated. Kept ``string`` deliberately so the
 * caller can pass a forward-compat slug without a type bump.
 */
export type ScopeDocumentSource =
  | ScopeFolderProvider
  | "notion"
  | "manual_upload"
  | "email_attachment"
  | "chat_attachment"
  | "ai_generated"
  | (string & {});

export interface ScopeDocumentItem {
  /** BE document id — matches Weaviate ``doc_id``. */
  readonly id: string;
  readonly display: string;
  /** Optional muted secondary line (folder path when available, else
   *  the provider name). The picker prefers ``folderPath`` over this
   *  when both are set so the row always anchors to "where it lives". */
  readonly subtitle?: string;
  /** White-label source slug — drives the badge label. */
  readonly provider?: ScopeDocumentSource;
  /** Full folder path inside the source (e.g. ``/Acme/Q3/Fatture``).
   *  When set, the picker shows it as the secondary line, falling back
   *  to ``subtitle`` only when absent. */
  readonly folderPath?: string;
}

export interface SourceScopePickerProps {
  // ── Folders slice ────────────────────────────────────────────────────
  readonly folderItems: readonly ScopeFolderItem[];
  readonly selectedFolderIds: readonly string[];
  readonly onChangeFolders: (ids: readonly string[]) => void;
  readonly foldersLoading?: boolean;
  /**
   * Drive providers the user has CONNECTED but for which **no folder
   * has been tracked yet** (= 0 indexable content). The picker renders
   * one explanatory row per provider with a "Traccia cartelle" CTA —
   * makes the partial-setup state legible instead of looking like a
   * bug ("Why don't I see Google Drive?").
   *
   * Caller computes this as ``connectedDriveProviders \ providersInFolderItems``.
   * Empty array (default) hides the section entirely.
   */
  readonly untrackedConnectedProviders?: ReadonlyArray<ScopeFolderProvider>;
  // ── Documents slice ──────────────────────────────────────────────────
  readonly selectedDocuments: readonly ScopeDocumentItem[];
  readonly onChangeDocuments: (docs: readonly ScopeDocumentItem[]) => void;
  /** Live query text. Caller debounces + drives ``documentResults``. */
  readonly documentQuery: string;
  readonly onDocumentQueryChange: (q: string) => void;
  readonly documentResults: readonly ScopeDocumentItem[];
  readonly documentsLoading?: boolean;
  // ── Structured-data slice (optional) ─────────────────────────────────
  /**
   * Enterprise / structured-data connectors (database, ERP, spreadsheet)
   * the user has linked. Promoted into the Ambito popover on June 11,
   * 2026 — pre-fix the only entry-point was clicking the "Focus ·
   * Strutturati" chip, which user feedback flagged as undiscoverable
   * ("poco chiaro per i clienti"). When ``structuredConnectors`` is
   * undefined the tab is hidden entirely (callers that don't ship the
   * capability stay unchanged — backward-compat). Empty array still
   * renders the tab so the explanatory empty state is reachable.
   */
  readonly structuredConnectors?: readonly ConnectorOption[];
  /** Optional single-source pilot; the caller owns connector availability. */
  readonly structuredSingleSelect?: boolean;
  readonly selectedStructuredIds?: readonly string[];
  readonly onChangeStructured?: (ids: readonly string[]) => void;
  // ── Common ───────────────────────────────────────────────────────────
  /** Opens the integrations surface. The optional ``provider`` argument
   *  is forwarded when the click came from a per-provider "Traccia
   *  cartelle" row so the caller can deep-link directly to that vendor.
   *  Also reused as the CTA when the structured-data tab is empty. */
  readonly onOpenIntegrations?: (provider?: ScopeFolderProvider) => void;
  /** Fires whenever the popover opens/closes — lets the caller
   *  gate live search hooks (``useMentionSearch``) so they don't
   *  keep firing once the popover is dismissed. */
  readonly onOpenChange?: (open: boolean) => void;
  readonly className?: string;
  /**
   * Optional ``data-tour`` value stamped on the trigger button so the
   * onboarding storyboard can spotlight the "Ambito" pill (where the user
   * picks a structured data source). Kept as an explicit prop — anchoring
   * a stable attribute to the real trigger, never a brittle class/role
   * selector (see ``features/onboarding/demoTourSteps.ts``).
   */
  readonly dataTour?: string;
}

type ActiveTab = "folders" | "documents" | "data";

export const SourceScopePicker: React.FC<SourceScopePickerProps> = ({
  folderItems,
  selectedFolderIds,
  onChangeFolders,
  foldersLoading = false,
  untrackedConnectedProviders = [],
  selectedDocuments,
  onChangeDocuments,
  documentQuery,
  onDocumentQueryChange,
  documentResults,
  documentsLoading = false,
  structuredConnectors,
  structuredSingleSelect = false,
  selectedStructuredIds,
  onChangeStructured,
  onOpenIntegrations,
  onOpenChange,
  className,
  dataTour,
}) => {
  // The "Dati" tab is only active when the caller wires the
  // structured-data slice — keeps the v1-style 2-tab call sites
  // unchanged and avoids a phantom tab on surfaces that never read
  // structured data (e.g. Studio drafts). The check looks at
  // ``onChangeStructured`` rather than ``structuredConnectors`` so an
  // empty array (= "no connectors yet, show empty-state CTA") still
  // surfaces the tab.
  const hasStructuredSlice = onChangeStructured !== undefined;
  const [open, setOpen] = React.useState(false);
  // ``Dati`` is the first tab when the structured-data slice is wired —
  // it's the discriminating affordance (folders/files are visible on
  // every surface; structured connectors are the new capability the
  // tab brings) and ICP-aligned (SMB users live on the gestionale +
  // CRM + accounting connectors, not on document folders).
  const [tab, setTab] = React.useState<ActiveTab>(
    hasStructuredSlice ? "data" : "folders",
  );
  const [folderFilter, setFolderFilter] = React.useState("");
  const [pendingFolders, setPendingFolders] = React.useState<readonly string[]>(selectedFolderIds);
  const [pendingDocuments, setPendingDocuments] =
    React.useState<readonly ScopeDocumentItem[]>(selectedDocuments);
  const [pendingStructured, setPendingStructured] = React.useState<readonly string[]>(
    selectedStructuredIds ?? [],
  );

  const prefersReducedMotion = useReducedMotion();

  // Forward open-state to the caller so it can pause its own live
  // search hooks (``useMentionSearch`` opt-in pattern).
  const handleOpenChange = React.useCallback(
    (next: boolean) => {
      setOpen(next);
      onOpenChange?.(next);
    },
    [onOpenChange],
  );

  // Mirror the external value when the popover (re)opens so the user
  // always starts from the latest authoritative state and Cancel-on-Esc
  // wipes only the in-flight pending mutation. ``selectedStructuredIds``
  // is read via ``?? []`` so callers that don't ship the slice still
  // hydrate the pending buffer to an empty list (cheap, idempotent).
  React.useEffect(() => {
    if (open) {
      setPendingFolders(selectedFolderIds);
      setPendingDocuments(selectedDocuments);
      setPendingStructured(selectedStructuredIds ?? []);
      setFolderFilter("");
    }
  }, [open, selectedFolderIds, selectedDocuments, selectedStructuredIds]);

  // Roving tabindex + ArrowLeft/Right/Home/End on the tablist.
  // WAI-ARIA APG 1.2 ``tabs`` pattern. Radix Popover focus-traps the
  // content but does not implement tab-roving — that's our job.
  const tablistRef = React.useRef<HTMLDivElement>(null);
  const folderSearchRef = React.useRef<HTMLInputElement>(null);
  const docSearchRef = React.useRef<HTMLInputElement>(null);

  // June 11, 2026 — generalised to the dynamic tab list so adding the
  // "Dati" segment (when the caller wires structured connectors) doesn't
  // require a third special-case branch. The ordered list is the
  // authoritative cursor; ArrowLeft/Right wraps, Home/End jump to the
  // ends. Matches the WAI-ARIA APG ``tabs`` pattern with the canonical
  // wrap-around semantics.
  const orderedTabs = React.useMemo<readonly ActiveTab[]>(
    () => (hasStructuredSlice ? ["data", "folders", "documents"] : ["folders", "documents"]),
    [hasStructuredSlice],
  );

  const onTablistKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key !== "ArrowLeft" && e.key !== "ArrowRight" && e.key !== "Home" && e.key !== "End") {
        return;
      }
      e.preventDefault();
      const len = orderedTabs.length;
      const idx = orderedTabs.indexOf(tab);
      let next: ActiveTab;
      if (e.key === "Home") next = orderedTabs[0] ?? tab;
      else if (e.key === "End") next = orderedTabs[len - 1] ?? tab;
      else if (e.key === "ArrowRight") next = orderedTabs[(idx + 1) % len] ?? tab;
      else next = orderedTabs[(idx - 1 + len) % len] ?? tab;
      setTab(next);
      tablistRef.current?.querySelector<HTMLButtonElement>(`#scope-tab-${next}`)?.focus();
    },
    [orderedTabs, tab],
  );

  // Initial focus is left to Radix Popover (lands on the first
  // focusable = the active tab button, matching the WAI-ARIA APG
  // ``tabs`` pattern). Users reach the search input via a single Tab
  // press. Auto-focusing the input would steal Home/End from the
  // tablist (the input swallows them as native cursor navigation).

  const pendingFolderSet = React.useMemo(() => new Set(pendingFolders), [pendingFolders]);
  const pendingDocSet = React.useMemo(
    () => new Set(pendingDocuments.map((d) => d.id)),
    [pendingDocuments],
  );

  const filteredFolders = React.useMemo(() => {
    const q = folderFilter.trim().toLowerCase();
    if (q.length === 0) return folderItems;
    return folderItems.filter(
      (it) => it.name.toLowerCase().includes(q) || (it.path ?? "").toLowerCase().includes(q),
    );
  }, [folderItems, folderFilter]);

  // Document rows are partitioned into two visual sections:
  //   1) "Selezionati" — the sticky/pending picks (always visible so
  //      deselect is one click away, even when out of live results).
  //   2) "Risultati" — live search hits, minus anything already pending.
  // This matches the SOTA pattern in Linear filter / Slack switcher.
  const { selectedRows, resultRows } = React.useMemo(() => {
    const pendingIds = new Set(pendingDocuments.map((d) => d.id));
    const remaining = documentResults.filter((d) => !pendingIds.has(d.id));
    return {
      selectedRows: pendingDocuments,
      resultRows: remaining,
    };
  }, [pendingDocuments, documentResults]);

  const toggleFolder = React.useCallback((id: string) => {
    setPendingFolders((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }, []);

  const toggleDocument = React.useCallback((doc: ScopeDocumentItem) => {
    setPendingDocuments((prev) => {
      const idx = prev.findIndex((d) => d.id === doc.id);
      return idx === -1 ? [...prev, doc] : prev.filter((d) => d.id !== doc.id);
    });
  }, []);

  const apply = React.useCallback(() => {
    onChangeFolders(pendingFolders);
    onChangeDocuments(pendingDocuments);
    // ``onChangeStructured`` is optional; calling through only when the
    // caller wired it keeps the v1-style 2-slice surface unchanged.
    onChangeStructured?.(pendingStructured);
    setOpen(false);
  }, [
    onChangeFolders,
    onChangeDocuments,
    onChangeStructured,
    pendingFolders,
    pendingDocuments,
    pendingStructured,
  ]);

  const clearAll = React.useCallback(() => {
    setPendingFolders([]);
    setPendingDocuments([]);
    setPendingStructured([]);
  }, []);

  const totalSelected =
    selectedFolderIds.length + selectedDocuments.length + (selectedStructuredIds?.length ?? 0);
  const pendingCount = pendingFolders.length + pendingDocuments.length + pendingStructured.length;
  // The empty-state copy lists the dimensions the user can restrict to,
  // so it MUST track ``hasStructuredSlice`` — surfacing "o dati" when the
  // tab isn't there would be a tiny but real lie. Once at least one
  // scope is picked, the label collapses to a count so the dimension
  // wording doesn't need to be plural-aware.
  const triggerLabel =
    totalSelected === 0
      ? hasStructuredSlice
        ? (structuredSingleSelect ? "Restringi a cartelle, file o fonti" : "Restringi a cartelle, file o dati")
        : "Restringi a cartelle o file"
      : totalSelected === 1
        ? "Ambito ristretto (1 elemento)"
        : `Ambito ristretto (${totalSelected} elementi)`;

  // Crossfade keeps spatial continuity on tab switch. Respects
  // ``prefers-reduced-motion`` (Framer Motion ``useReducedMotion``
  // returns ``true`` → durations zero out).
  const panelMotion = prefersReducedMotion
    ? { initial: false, animate: { opacity: 1 }, transition: { duration: 0 } }
    : {
        initial: { opacity: 0, y: 2 },
        animate: { opacity: 1, y: 0 },
        exit: { opacity: 0, y: -2 },
        transition: { duration: 0.12, ease: "easeOut" as const },
      };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-tour={dataTour}
          aria-label={triggerLabel}
          aria-haspopup="dialog"
          aria-expanded={open}
          // Compact toolbar: icon-only when nothing is scoped (the native
          // tooltip names it; the popover trigger can't nest a hover-Tooltip);
          // expands to the "Ambito" label + count badge once a scope is set.
          title={totalSelected > 0 ? undefined : "Ambito"}
          className={cn(
            // Icon-only minimal button aligned with the other composer icon
            // controls (mic / Cerca a fondo): h-8, rounded-lg, borderless.
            // Expands to a soft-orange pill with label + count once scoped.
            "inline-flex h-8 shrink-0 items-center gap-1.5 rounded-lg font-medium text-[11px] transition-colors",
            totalSelected > 0
              ? "bg-ando-orange-soft px-2.5 text-ando-orange hover:bg-ando-orange/15"
              : "w-8 justify-center bg-transparent text-ink-3 hover:bg-black/[0.045] hover:text-ink-1",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/45",
            className,
          )}
        >
          <Folder aria-hidden className="h-3.5 w-3.5" />
          {totalSelected > 0 ? <span>Ambito</span> : null}
          {totalSelected > 0 ? (
            <span
              aria-hidden
              className="inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-ando-orange px-1 font-semibold text-[10px] text-white"
            >
              {totalSelected}
            </span>
          ) : null}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={8}
        className="w-80 p-0"
        aria-label="Ambito della ricerca"
      >
        {/* Tabs — single tablist, two segments, count badges.
            Roving tabindex + Arrow/Home/End handled by the tablist
            keydown listener. */}
        <div
          role="tablist"
          ref={tablistRef}
          onKeyDown={onTablistKeyDown}
          aria-label="Tipo di ambito"
          className="flex border-hairline border-b"
        >
          {hasStructuredSlice ? (
            <ScopeTabButton
              active={tab === "data"}
              onClick={() => setTab("data")}
              count={pendingStructured.length}
              icon={<Database className="h-3.5 w-3.5" aria-hidden />}
              label={structuredSingleSelect ? "Fonti" : "Dati"}
              tabId="scope-tab-data"
              panelId="scope-panel-data"
            />
          ) : null}
          <ScopeTabButton
            active={tab === "folders"}
            onClick={() => setTab("folders")}
            count={pendingFolders.length}
            icon={<Folder className="h-3.5 w-3.5" aria-hidden />}
            label="Cartelle"
            tabId="scope-tab-folders"
            panelId="scope-panel-folders"
          />
          <ScopeTabButton
            active={tab === "documents"}
            onClick={() => setTab("documents")}
            count={pendingDocuments.length}
            icon={<FileText className="h-3.5 w-3.5" aria-hidden />}
            label="File"
            tabId="scope-tab-documents"
            panelId="scope-panel-documents"
          />
        </div>

        {/* ``initial={false}`` skips the enter animation on the first
            render so the popover opens snappy. No ``mode="wait"`` —
            crossfade is short enough that brief overlap reads as a
            smooth swap rather than a jank, and jsdom-based tests don't
            need to wait for an exit animation to mount the new panel. */}
        <AnimatePresence initial={false}>
          {tab === "data" && hasStructuredSlice ? (
            <motion.div
              key="data"
              {...panelMotion}
              role="tabpanel"
              id="scope-panel-data"
              aria-labelledby="scope-tab-data"
              className="px-2 py-2"
            >
              {(structuredConnectors?.length ?? 0) === 0 ? (
                // Empty state mirrors the Folders tab affordance — single
                // CTA that hands the user off to /integrations, where the
                // database / ERP / spreadsheet wizards live.
                <ScopePickerEmpty
                  title={structuredSingleSelect ? "Nessuna fonte collegata" : "Nessun connettore dati"}
                  subtitle={structuredSingleSelect ? "Gestisci le integrazioni per aggiungere una fonte." : "Collega un database, un ERP o un foglio per usarli come fonte strutturata."}
                  onOpenIntegrations={onOpenIntegrations ? () => onOpenIntegrations() : undefined}
                />
              ) : (
                <>
                  {/* June 11, 2026 — micro-explainer (UX review SOTA
                      pattern). Risponde al dubbio "vengono davvero
                      interrogati?" prima ancora che l'utente lo formi:
                      la scelta del connettore = la fonte viene usata,
                      punto. Niente "Tipo di risposta" da settare a
                      mano. Pattern Glean (intent-led scope) +
                      Perplexity Spaces (sources-as-context). */}
                  <p role="note" className="px-3 pb-1.5 text-[10.5px] text-ink-3 leading-tight">
                    {structuredSingleSelect ? "Scegli una fonte. La disponibilità dipende dal motore selezionato in Tools." : "Ando interrogherà i connettori selezionati come fonte. Nessuna modalità da attivare."}
                  </p>
                  {/* "Seleziona tutti" shortcut — risposta al pain
                      point "voglio usare TUTTI i miei strutturati
                      senza dover cliccare 20 checkbox". Toggle
                      semantica (Seleziona ↔ Deseleziona) anziché due
                      bottoni paralleli: il footer del ConnectorPicker
                      ha già il proprio "Deseleziona tutto" ma quello
                      è visibile solo quando count > 0, quindi questo
                      bottone copre lo stato "0 → N" che il footer
                      lascia scoperto. Salta i connettori
                      ``disabled`` (non raggiungibili). */}
                  {!structuredSingleSelect && (() => {
                    const enabledIds = (structuredConnectors ?? [])
                      .filter((c) => !c.disabled)
                      .map((c) => c.id);
                    const allSelected =
                      enabledIds.length > 0 &&
                      enabledIds.every((id) => pendingStructured.includes(id));
                    return (
                      <div className="flex items-center justify-end px-3 pb-1.5">
                        <button
                          type="button"
                          onClick={() => setPendingStructured(allSelected ? [] : enabledIds)}
                          disabled={enabledIds.length === 0}
                          className={cn(
                            "font-medium text-[10.5px] transition-colors",
                            "text-ink-2 hover:text-ando-orange",
                            "disabled:cursor-not-allowed disabled:opacity-40",
                            "focus-visible:underline focus-visible:outline-none",
                          )}
                        >
                          {allSelected ? "Deseleziona tutti" : "Seleziona tutti"}
                        </button>
                      </div>
                    );
                  })()}
                  {/* DRY — reuse the existing ``ConnectorPicker`` primitive
                      (tri-state grouping by P.IVA, category badge, internal
                      search). We feed it the pending buffer so Apply commits
                      atomically alongside folders/documents and Esc/outside-
                      click cancel-rollback semantics match the other tabs. */}
                  <ConnectorPicker
                    connectors={[...(structuredConnectors ?? [])]}
                    selectedIds={[...pendingStructured]}
                    allowGroupSelection={!structuredSingleSelect}
                    onChange={(next) => {
                      if (!structuredSingleSelect) { setPendingStructured(next); return; }
                      const added = next.filter((id) => !pendingStructured.includes(id));
                      if (added.length > 1) return;
                      setPendingStructured(added.length === 1 ? added : next);
                    }}
                  />
                </>
              )}
            </motion.div>
          ) : tab === "folders" ? (
            <motion.div
              key="folders"
              {...panelMotion}
              role="tabpanel"
              id="scope-panel-folders"
              aria-labelledby="scope-tab-folders"
            >
              <ScopeSearchBox
                ref={folderSearchRef}
                value={folderFilter}
                onChange={setFolderFilter}
                ariaLabel="Cerca cartella"
                placeholder="Cerca cartella…"
              />
              <ul
                role="group"
                aria-label="Cartelle disponibili"
                className="m-0 max-h-72 list-none overflow-y-auto p-0 py-1"
              >
                {foldersLoading ? (
                  <ScopePickerSkeleton />
                ) : folderItems.length === 0 && untrackedConnectedProviders.length === 0 ? (
                  <ScopePickerEmpty
                    title="Nessuna cartella collegata"
                    subtitle="Connetti un Drive per filtrare le risposte per cartella."
                    onOpenIntegrations={onOpenIntegrations ? () => onOpenIntegrations() : undefined}
                  />
                ) : (
                  <>
                    {filteredFolders.length === 0 && folderItems.length > 0 ? (
                      <ScopeNoResults
                        query={folderFilter}
                        onClearFilter={() => setFolderFilter("")}
                      />
                    ) : (
                      filteredFolders.map((it) => {
                        const checked = pendingFolderSet.has(it.id);
                        return (
                          <li key={it.id}>
                            <ScopeRow
                              checked={checked}
                              onToggle={() => toggleFolder(it.id)}
                              icon={
                                <Folder aria-hidden className="h-3.5 w-3.5 shrink-0 text-ink-3" />
                              }
                              primary={it.name}
                              secondary={it.path && it.path !== it.name ? it.path : undefined}
                              badge={SCOPE_PROVIDER_LABEL[it.provider]}
                              ariaLabel={`${it.name}${it.path ? ` · ${it.path}` : ""}`}
                            />
                          </li>
                        );
                      })
                    )}

                    {/* Per-provider "connected but no tracked folders" rows
                        — makes the partial-setup state legible instead of
                        looking like a bug ("why don't I see Google Drive?").
                        Only shown when the folder-filter input is empty so
                        a user-driven search doesn't get polluted. */}
                    {folderFilter.trim().length === 0 && untrackedConnectedProviders.length > 0 ? (
                      <>
                        {folderItems.length > 0 ? (
                          <li
                            role="presentation"
                            aria-hidden
                            className="my-1 border-hairline border-t"
                          />
                        ) : null}
                        {untrackedConnectedProviders.map((prv) => (
                          <li key={`untracked-${prv}`}>
                            <ScopeUntrackedProviderRow
                              provider={prv}
                              onTrack={() => onOpenIntegrations?.(prv)}
                            />
                          </li>
                        ))}
                      </>
                    ) : null}
                  </>
                )}
              </ul>
            </motion.div>
          ) : (
            <motion.div
              key="documents"
              {...panelMotion}
              role="tabpanel"
              id="scope-panel-documents"
              aria-labelledby="scope-tab-documents"
            >
              <ScopeSearchBox
                ref={docSearchRef}
                value={documentQuery}
                onChange={onDocumentQueryChange}
                ariaLabel="Cerca file"
                placeholder="Cerca un file…"
              />
              <ul
                role="group"
                aria-label="File disponibili"
                className="m-0 max-h-72 list-none overflow-y-auto p-0 py-1"
              >
                {documentsLoading && selectedRows.length === 0 && resultRows.length === 0 ? (
                  <ScopePickerSkeleton />
                ) : selectedRows.length === 0 && resultRows.length === 0 ? (
                  <ScopeNoResults
                    query={documentQuery}
                    onClearFilter={
                      documentQuery.length > 0 ? () => onDocumentQueryChange("") : undefined
                    }
                    fallbackHint="Digita per cercare un file…"
                  />
                ) : (
                  <>
                    {selectedRows.length > 0 ? (
                      <ScopeSectionHeader label="Selezionati" count={selectedRows.length} />
                    ) : null}
                    {selectedRows.map((doc) => (
                      <li key={`pending-${doc.id}`}>
                        <ScopeRow
                          checked={pendingDocSet.has(doc.id)}
                          onToggle={() => toggleDocument(doc)}
                          icon={
                            <FileText aria-hidden className="h-3.5 w-3.5 shrink-0 text-ink-3" />
                          }
                          primary={doc.display}
                          secondary={doc.folderPath ?? doc.subtitle}
                          badge={scopeDocSourceLabel(doc.provider) ?? undefined}
                          ariaLabel={buildDocAriaLabel(doc)}
                        />
                      </li>
                    ))}
                    {resultRows.length > 0 && documentQuery.length > 0 ? (
                      <ScopeSectionHeader label="Risultati" count={resultRows.length} />
                    ) : null}
                    {resultRows.map((doc) => (
                      <li key={doc.id}>
                        <ScopeRow
                          checked={pendingDocSet.has(doc.id)}
                          onToggle={() => toggleDocument(doc)}
                          icon={
                            <FileText aria-hidden className="h-3.5 w-3.5 shrink-0 text-ink-3" />
                          }
                          primary={doc.display}
                          secondary={doc.folderPath ?? doc.subtitle}
                          badge={scopeDocSourceLabel(doc.provider) ?? undefined}
                          ariaLabel={buildDocAriaLabel(doc)}
                        />
                      </li>
                    ))}
                  </>
                )}
              </ul>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="flex items-center justify-between gap-2 border-hairline border-t px-3 py-2">
          <button
            type="button"
            onClick={clearAll}
            disabled={pendingCount === 0}
            className={cn(
              "rounded-md px-2 py-1 text-[11px] text-ink-2 transition-colors",
              "hover:bg-ink-1/[0.06] disabled:cursor-not-allowed disabled:opacity-40",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/45",
            )}
          >
            Rimuovi tutto
          </button>
          <button
            type="button"
            onClick={apply}
            className={cn(
              "rounded-md bg-ando-orange px-3 py-1 font-medium text-[11px] text-white transition-colors hover:bg-ando-orange/90",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/45",
            )}
          >
            Applica
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
};
SourceScopePicker.displayName = "SourceScopePicker";

// ─── Local subcomponent ───────────────────────────────────────────────────
// ScopeTabButton stays local because it carries a unique tab/panel id
// pair and roving-tabindex semantics that aren't reusable outside this
// compound — extracting it would require leaking ARIA wiring into the
// shared module without DRY benefit.

interface ScopeTabButtonProps {
  readonly active: boolean;
  readonly onClick: () => void;
  readonly count: number;
  readonly icon: React.ReactElement;
  readonly label: string;
  readonly tabId: string;
  readonly panelId: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────

/**
 * Build a rich aria-label for a document row that bakes the provenance
 * (source + folder path) into the checkbox name. Screen-reader users
 * hear "FT-094.pdf, Google · /Acme/Q3" instead of just the filename —
 * removes the ambiguity the visual badge fixed for sighted users.
 */
function buildDocAriaLabel(doc: ScopeDocumentItem): string {
  const sourceLabel = scopeDocSourceLabel(doc.provider);
  const parts = [doc.display];
  if (sourceLabel) parts.push(sourceLabel);
  if (doc.folderPath) parts.push(doc.folderPath);
  return parts.join(" · ");
}

// ─── Untracked-provider row ───────────────────────────────────────────────
// Shown when the user has a Drive connected but hasn't tracked any folders
// from it yet — common Q&A trigger ("why don't I see Google Drive?").
// Visually distinct from a tracked-folder row (muted, no checkbox, CTA).

interface ScopeUntrackedProviderRowProps {
  readonly provider: ScopeFolderProvider;
  readonly onTrack: () => void;
}

function ScopeUntrackedProviderRow({
  provider,
  onTrack,
}: ScopeUntrackedProviderRowProps): React.ReactElement {
  const label = SCOPE_PROVIDER_LABEL[provider];
  return (
    <div className="flex items-center gap-2 px-3 py-1.5">
      <Folder aria-hidden className="h-3.5 w-3.5 shrink-0 text-ink-3/60" />
      <span className="flex min-w-0 flex-1 flex-col leading-tight">
        <span className="truncate text-[11.5px] text-ink-2">
          {label}: nessuna cartella tracciata
        </span>
        <span className="truncate text-[10px] text-ink-3">
          Indicizza una cartella per filtrare le risposte.
        </span>
      </span>
      <button
        type="button"
        onClick={onTrack}
        className={cn(
          "shrink-0 rounded-md border border-hairline bg-white px-2 py-0.5 font-medium text-[10.5px] text-ink-1 transition-colors",
          "hover:bg-ink-1/[0.04]",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/45",
        )}
        aria-label={`Traccia cartelle in ${label}`}
      >
        Traccia
      </button>
    </div>
  );
}

function ScopeTabButton({
  active,
  onClick,
  count,
  icon,
  label,
  tabId,
  panelId,
}: ScopeTabButtonProps): React.ReactElement {
  return (
    <button
      type="button"
      role="tab"
      id={tabId}
      aria-selected={active}
      aria-controls={panelId}
      // Roving tabindex (WAI-ARIA APG ``tabs`` pattern): only the
      // active tab participates in Tab-key flow; inactive tabs are
      // reached via Arrow keys via the tablist keydown handler.
      tabIndex={active ? 0 : -1}
      onClick={onClick}
      className={cn(
        "inline-flex flex-1 items-center justify-center gap-1.5 border-b-2 px-3 py-2 font-medium text-[11px] transition-colors",
        active
          ? "border-ando-orange text-ando-orange"
          : "border-transparent text-ink-2 hover:bg-ink-1/[0.04] hover:text-ink-1",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/45",
      )}
    >
      {icon}
      <span>{label}</span>
      {count > 0 ? (
        <span
          aria-hidden
          className={cn(
            "inline-flex h-4 min-w-4 items-center justify-center rounded-full px-1 font-semibold text-[10px]",
            active ? "bg-ando-orange text-white" : "bg-ink-1/10 text-ink-2",
          )}
        >
          {count}
        </span>
      ) : null}
    </button>
  );
}
